import { convertToLocalTime, convertToLocalTimeUnix } from '@/composable/useFormatDateAndHours'
import { useLineChart } from '@/composables/useLineChart'
import type { IGroupTotalizer, ISensorTotalizer } from '@/interfaces/stores/dashboard'
import { useDashboardStore } from '@/store/dashboard'
import { formatMinutesToHoursWithReadbleMinutes } from '@/utils/date/date-time'
import { storeToRefs } from 'pinia'

export const formatTotalizerValue = (key: string, value: number | string) => {
  switch (key) {
    case 'utilization_percentage':
    case 'utilization':
      return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}%`
    case 'total_non_utilization_time':
    case 'total_utilization_time':
      return formatMinutesToHoursWithReadbleMinutes(value as number)
    case 'total_consumption_kwh':
    case 'peak_consumption_kwh':
    case 'off_peak_consumption_kwh':
      return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kWh`
    case 'peak_demand_max':
    case 'off_peak_demand_max':
      return `${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kW`
    default:
      return value
  }
}

export const formatGroupTotalizers = (totalizerParams: IGroupTotalizer) => {
  return Object.entries(totalizerParams)
    .filter(([_, value]) => !Array.isArray(value))
    .map(([key, value]) => ({
      value: formatTotalizerValue(key, value),
      label: key,
    }))
}

export const formatSensorTotalizers = (totalizerParams: ISensorTotalizer) => {
  const { consumption, demand, utilization } = totalizerParams

  const totalizer = [
    { key: 'total_consumption_kwh', value: consumption.total_consumption_kwh },
    { key: 'peak_consumption_kwh', value: consumption.peak_consumption_kwh },
    { key: 'off_peak_consumption_kwh', value: consumption.off_peak_consumption_kwh },
    { key: 'peak_demand_max', value: demand.peak_demand_kw },
    { key: 'off_peak_demand_max', value: demand.off_peak_demand_kw },
    { key: 'total_non_utilization_time', value: utilization.total_non_utilization_time },
    { key: 'total_utilization_time', value: utilization.total_utilization_time },
    { key: 'utilization', value: utilization.utilization },
  ]

  return totalizer.map(({ key, value }) => ({
    value: formatTotalizerValue(key, value),
    label: key,
  }))
}

export interface IFormatDashboardChartParamsOptions {
  period?: Date[]
  strokeColors?: string[]
  chartIdSuffix?: string
  xaxisLabelsRotate?: number
}

export const formatDashboardChartParams = (
  readings: any[],
  sensorName: string,
  isSensor: boolean = false,
  options?: IFormatDashboardChartParamsOptions,
) => {
  const dashboardStore = useDashboardStore()
  const { period: storePeriod } = storeToRefs(dashboardStore)

  const seriesParams: Array<{}> = []
  let chartOptionsParams: object

  const periodSource = options?.period?.length ? options.period : storePeriod.value || []
  const [startDate, endDate] = periodSource
  const toChartUnix = (value: Date | string) =>
    convertToLocalTimeUnix(value instanceof Date ? value : new Date(value))
  const min = startDate ? toChartUnix(startDate) : undefined
  const max = endDate ? toChartUnix(endDate) : undefined

  if (isSensor) {
    seriesParams.push({
      name: sensorName,
      data: readings.map((reading) => ({
        x: convertToLocalTime(reading.time),
        y: Number(reading.total_power),
      })),
    })
  } else {
    readings.forEach((reading) => {
      seriesParams.push({
        name: reading.sensor_name,
        data: reading.readings.map((r: any) => ({
          x: convertToLocalTime(r.time),
          y: Number(r.total_power),
        })),
      })
    })
  }

  const chartId = options?.chartIdSuffix ? `dashboard-demand-chart-${options.chartIdSuffix}` : 'dashboard-demand-chart'

  const { chartOptions } = useLineChart({
    chartId,
    exportConfig: {
      filename: 'dashboard-demanda',
      unit: 'kW',
    },
    yAxisFormatter: (value: number) => `${value.toFixed(2)} kW`,
    tooltipYFormatter: (value: number) => `${value?.toFixed(2)} kW`,
    unit: 'kW',
  })

  const colors = options?.strokeColors?.length ? options.strokeColors : undefined

  const baseChart = (chartOptions.value as { chart?: Record<string, unknown> }).chart ?? {}
  chartOptionsParams = {
    ...chartOptions.value,
    ...(colors ? { colors } : {}),
    chart: {
      ...baseChart,
      redrawOnParentResize: true,
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      fontSize: '14px',
      fontWeight: 'bold',
      labels: {
        useSeriesColors: true,
      },
      position: 'bottom',
      offsetY: 10,
    },
    stroke: {
      width: 3,
      curve: 'straight',
    },
    xaxis: {
      ...chartOptions.value.xaxis,
      min,
      max,
      labels: {
        ...(chartOptions.value.xaxis as { labels?: object }).labels,
        rotate: options?.xaxisLabelsRotate ?? 0,
      },
    },
  }

  return { seriesParams, chartOptionsParams }
}
